import React, {
	Dispatch,
	Fragment,
	SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import {
	close,
	pause as pauseIcon,
	play as playIcon,
	upgrade,
	warning,
} from '@/assets/icons';
// import { Scrollbar } from '@/components/Scrollbar';
import { AsaphButton } from '@/components/shared/Button';
import { Loader } from '@/components/shared/Loader';
import { LinkFrom } from '@/config/constants';
import { dashboardUrl, videoUrl as videoUrlConst } from '@/config/urls';
import { Maybe } from '@/graphql';
// import MarkdownPreview from '@uiw/react-markdown-preview';
// import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import { timeFormatter } from '@/helpers/timeFormatter';
import { useIsMounted } from '@/hooks/useIsMounted';
import { useSubscription } from '@/providers/SubscriptionProvider';
import { Capacitor } from '@capacitor/core';
import { Slider, SvgIcon } from '@material-ui/core';
import Hls from 'hls.js';
import { isNil } from 'ramda';

import { ControlButton } from '../ControlButton';
// import { formatTimeRemaining } from '@/helpers/formatTimeRemaining';
import { IconButton } from '../IconButton';
// import { TimeRemaining } from './Devotion/TimeRemaining';
// import { MiniControls } from '../MiniControls';
import { PrevNextButton } from '../PrevNextButton/';
import './Player.scss';

interface IProps {
	videoId: string;
	videoUrl: string | null;
	percentagePlayed: number;
	/** Start the video at x seconds */
	startVideo?: number;
	posterUrl?: Maybe<string>;
	headerElement?: JSX.Element;
	footerElement?: JSX.Element;
	onPercentagePlayedChange?(value: number): void;
	onVideoEnd?(): void;
	synopsisShown: boolean;
	displayLeftColumn: boolean;
	displayRightColumn: boolean;
	chatOpen: boolean;
	chatExpanded: boolean;
	// isDevotion: boolean;
	// devotionTextShown: boolean;
	// devotionText?: string;
	// devotionTextFooter?: string;
	signedQueryParams?: string | null;
	upNextShown?: boolean;
	setUpNextShown: Dispatch<boolean>;
	prevVideo: string | null;
	nextVideo: string | null;
	onCloseClick: () => void;
	isPaidContent: boolean;
	linkFrom: LinkFrom;
}

export const Player: React.FC<IProps> = ({
	videoId,
	videoUrl,
	percentagePlayed,
	startVideo,
	posterUrl,
	headerElement,
	footerElement,
	onPercentagePlayedChange,
	onVideoEnd,
	synopsisShown,
	displayLeftColumn,
	displayRightColumn,
	chatOpen,
	chatExpanded,
	// isDevotion,
	// devotionTextShown,
	// devotionText,
	// devotionTextFooter,
	signedQueryParams,
	upNextShown,
	setUpNextShown,
	prevVideo,
	nextVideo,
	onCloseClick,
	isPaidContent,
	linkFrom,
}) => {
	const isMounted = useIsMounted();
	const isCapacitor = Capacitor.isNativePlatform();
	const { setPaidModalOpen } = useSubscription();

	const [hls] = useState<Hls>(
		new Hls({
			xhrSetup: (xhr) => {
				xhr.withCredentials = false; // do not send cookies
				xhr.setRequestHeader(
					'Access-Control-Allow-Headers',
					'X-Requested-With, Content-Type, Accept',
				);
				xhr.setRequestHeader(
					'Access-Control-Allow-Origin',
					window.location.origin,
				);
				xhr.setRequestHeader(
					'Access-Control-Allow-Credentials',
					'false',
				);
			},
			startLevel: 3,
		}),
	);
	const videoElement = useRef<HTMLVideoElement>(null);
	const [videoReady, setVideoReady] = useState<boolean>(false);
	const [videoEnded, setVideoEnded] = useState<boolean>(false);
	const [playerSupported, setPlayerSupported] = useState<boolean>(true);
	const [controlsActive, setControlsActive] = useState<boolean>(false);
	const [isPaused, setIsPaused] = useState<boolean>(true);
	const [playError, setPlayError] = useState<boolean>(false);
	// remains for a possible later use
	// const [isMuted, setIsMuted] = useState<boolean>(false);
	// const [currentTime, setCurrentTime] = useState<string>('0:00');
	const [remainingTime, setRemainingTime] = useState<string>('0:00');
	const [playPercentage, setPlayPercentage] =
		useState<number>(percentagePlayed);

	// const storedDevotionVideoId = useDevotionStore(
	// 	useCallback((state) => state.devotion?.video.id, []),
	// );
	// const endDay = useDevotionStore(useCallback((state) => state.endDay, []));

	// let remainingDevotionTime = '';
	// if (videoId === storedDevotionVideoId) {
	// 	remainingDevotionTime = `${formatTimeRemaining(
	// 		differenceInMilliseconds(endDay, new Date()),
	// 	)} remaining`;
	// } else {
	// 	remainingDevotionTime = 'This devotion has ended';
	// }

	useEffect(() => {
		return () => {
			hls.destroy();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (
			videoElement.current &&
			!videoElement.current.canPlayType(
				'application/vnd.apple.mpegurl',
			) &&
			!Hls.isSupported()
		) {
			setPlayerSupported(false);
		}
	}, [videoElement]);

	useEffect(() => {
		if (videoElement.current && isMounted.current) {
			if (videoUrl && Hls.isSupported()) {
				hls.loadSource(videoUrl);
				hls.attachMedia(videoElement.current);
				hls.on(Hls.Events.MANIFEST_PARSED, initialVideoPlay);
			} else if (
				videoElement.current.canPlayType(
					'application/vnd.apple.mpegurl',
				)
			) {
				// Case for Safari
				videoElement.current.src = videoUrl + '?' + signedQueryParams;
				videoElement.current.addEventListener(
					'loadedmetadata',
					initialVideoPlay,
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoUrl]);

	useEffect(() => {
		if (videoElement.current) {
			const video = videoElement.current;

			// Attach the events listener to the video element
			video.addEventListener('durationchange', () => {
				if (isMounted.current) {
					setRemainingTime(
						timeFormatter(video.duration - video.currentTime),
					);
					setPlayPercentage(video.currentTime / video.duration);
				}
			});
			video.addEventListener('play', () => {
				if (isMounted.current) {
					setVideoEnded(false);
					setIsPaused(false);
				}
			});
			video.addEventListener('pause', () => {
				if (isMounted.current) {
					setIsPaused(true);
				}
			});
			// remains for a possible later use
			// video.addEventListener('volumechange', (e) => {
			// 	if (isMounted.current) {
			// 		setIsMuted(!!video.muted);
			// 	}
			// });
			video.addEventListener('timeupdate', () => {
				if (isMounted.current) {
					setVideoEnded(false);
					// remains for a possible later use
					// setCurrentTime(timeFormatter(video.currentTime));
					setRemainingTime(
						timeFormatter(video.duration - video.currentTime),
					);
					setPlayPercentage(video.currentTime / video.duration);
				}
			});
			video.addEventListener('ended', () => {
				if (isMounted.current) {
					if (!isNil(onVideoEnd)) {
						onVideoEnd();
					}

					setVideoEnded(true);
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoElement]);

	useEffect(() => {
		if (onPercentagePlayedChange && !isPaidContent) {
			onPercentagePlayedChange(playPercentage);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [playPercentage, isPaidContent]);

	const classes = ['player'];
	if (controlsActive) {
		classes.push('player--controls-active');
	}

	const classesVideo = ['player-video'];
	if (videoReady) {
		classesVideo.push('player-video--ready');
	}
	// if (devotionTextShown && !chatOpen) {
	// 	classesVideo.push('player-video--minified');
	// 	if (displayLeftColumn) {
	// 		classesVideo.push('player-video--minified-fixed');
	// 	}
	// }

	const classesVideoWrapperHeight = ['player-video__wrapper-height'];
	if (!displayRightColumn) {
		classesVideoWrapperHeight.push(
			'player-video__wrapper-height--withoutColumns',
		);
	}
	if (!displayRightColumn && chatOpen) {
		classesVideoWrapperHeight.push(
			'player-video__wrapper-height--withChatOpen',
		);
	}

	const classesVideoWrapper = ['player-video__wrapper'];
	// if (devotionTextShown && displayLeftColumn) {
	// 	classesVideoWrapper.push('player-video__wrapper--faded');
	// }

	const controlsListenerClasses = ['player__controls-listener'];
	if (controlsActive) {
		controlsListenerClasses.push('player__controls-listener--disabled');
	}
	if (synopsisShown) {
		controlsListenerClasses.push(
			'player__controls-listener--disabledWithSynopsisShown',
		);
	}
	// if (devotionTextShown) {
	// 	controlsListenerClasses.push('player__controls-listener--hidden');
	// }
	if (!displayRightColumn && chatOpen && !chatExpanded) {
		controlsListenerClasses.push('player__controls-listener--withChatOpen');
	}

	const headerClasses = ['player__header-container'];
	if (synopsisShown) {
		headerClasses.push('player__header-container--expanded');
	}
	if (!displayRightColumn && chatOpen && !chatExpanded) {
		headerClasses.push('player__header-container--withChatOpen');
	}

	const footerClasses = ['player__footer'];
	if (controlsActive) {
		footerClasses.push('player__footer--hidden');
	}

	const scrubberClasses = ['player-scrubber'];
	if (!displayRightColumn && chatOpen && !chatExpanded) {
		scrubberClasses.push('player-scrubber--withChatOpen');
	}

	const scrubber = !isPaidContent ? (
		<div className={scrubberClasses.join(' ')}>
			<div className="player-scrubber__play">
				<ControlButton
					icon={isPaused ? playIcon : pauseIcon}
					ariaLabel={isPaused ? 'play' : 'paused'}
					size="xsmall"
					onClick={togglePlayPause}
				/>
			</div>
			<div className="player-scrubber__slider">
				<Slider
					value={playPercentage}
					onChange={seek}
					step={0.00000001}
					min={0}
					max={1}
				/>
			</div>
			<div className="player-scrubber__time player-scrubber__time--remaining">
				{remainingTime}
			</div>
		</div>
	) : null;

	// const markdownContent = useMemo(
	// 	() => (
	// 		<MarkdownPreview
	// 			source={devotionText}
	// 			prefixCls=""
	// 			linkTarget="_blank"
	// 		/>
	// 	),
	// 	[devotionText],
	// );

	return (
		<div className={classes.join(' ')}>
			<div className="player__content">
				{!playerSupported ? (
					<div className="player-error">
						<SvgIcon
							component={warning}
							className="player-error__icon"
						/>
						<div className="player-error__text">
							Unfortunately your browser doesn't support HLS
							files.
						</div>
					</div>
				) : (
					<Fragment>
						<div className={classesVideoWrapperHeight.join(' ')}>
							<div className={classesVideoWrapper.join(' ')}>
								<video
									className={classesVideo.join(' ')}
									ref={videoElement}
									poster={posterUrl ?? undefined}
									autoPlay={!isPaidContent && !playError}
									playsInline={true}
								/>
								{!displayLeftColumn && (
									<>
										<div
											className="player__controls"
											onClick={handlePlayerControlsClick}
										>
											{!!headerElement && (
												<div
													className={headerClasses.join(
														' ',
													)}
												>
													{headerElement}
												</div>
											)}
											{/* {isDevotion &&
												!displayLeftColumn &&
												!synopsisShown && (
													<div className="player__devotion-time-remaining">
														<TimeRemaining
															value={
																remainingDevotionTime
															}
															ended={
																videoId !==
																storedDevotionVideoId
															}
														/>
													</div>
												)} */}
											{!displayRightColumn &&
												!synopsisShown &&
												!upNextShown &&
												scrubber}
										</div>
										<div
											className={footerClasses.join(' ')}
										>
											{!!footerElement && footerElement}
											{/* {!displayRightColumn &&
												!chatOpen &&
												devotionTextShown && (
													<MiniControls
														isPaused={isPaused}
														onPlayClick={
															togglePlayPause
														}
														onBackwardClick={back}
														onForwardClick={forward}
													/>
												)} */}
										</div>
										<div
											className={controlsListenerClasses.join(
												' ',
											)}
											onClick={() => {
												// if (!devotionTextShown) {
												setControlsActive(
													!controlsActive,
												);
												setUpNextShown(false);
												// }
											}}
										/>
									</>
								)}
								{/* {devotionTextShown && !chatOpen && (
									<Scrollbar className="player__devotion-text">
										{!isNil(devotionText) && (
											<div className="player__devotion-text-content">
												{markdownContent}
											</div>
										)}
										{!isNil(devotionTextFooter) && (
											<div className="player__devotion-text-footer">
												{devotionTextFooter}
											</div>
										)}
									</Scrollbar>
								)} */}
								{displayRightColumn && !isNil(prevVideo) && (
									<div className="player__prev-wrapper">
										<PrevNextButton
											type="prev"
											link={`/videos/${prevVideo}`}
										/>
									</div>
								)}
								{displayRightColumn && !isNil(nextVideo) && (
									<div className="player__next-wrapper">
										<PrevNextButton
											type="next"
											link={`/videos/${nextVideo}`}
										/>
									</div>
								)}
								{videoReady &&
									!playError &&
									videoEnded &&
									!displayRightColumn && (
										<div className="player__centered-close">
											<IconButton
												icon={close}
												color="transparent"
												onClick={onCloseClick}
												size="medium"
											/>
										</div>
									)}
							</div>
							{displayRightColumn && scrubber}
							{!isPaidContent && (
								<div className="player__centered">
									{!videoReady ? (
										<Loader />
									) : playError ? (
										<IconButton
											icon={playIcon}
											color="transparent"
											onClick={play}
											size="large"
										/>
									) : videoEnded ? (
										<div className="player__centered-buttons">
											<AsaphButton
												label="Watch again"
												color="blue"
												fullWidth
												onClick={play}
												className={`player__centered-button${
													!isNil(nextVideo)
														? '--transparent'
														: ''
												}`}
											/>
											{!isNil(nextVideo) ? (
												<AsaphButton
													label="Watch next"
													color="grey"
													fullWidth
													to={videoUrlConst.replace(
														':token',
														nextVideo,
													)}
													className="player__centered-button"
												/>
											) : (
												<AsaphButton
													label="Back to dashboard"
													color="grey"
													fullWidth
													to={dashboardUrl}
													className="player__centered-button"
												/>
											)}
										</div>
									) : null}
								</div>
							)}
							{isPaidContent && (
								<div className="player__paid-content">
									<AsaphButton
										color="blue"
										label="Upgrade"
										icon={
											<SvgIcon
												component={upgrade}
												color="disabled"
												fontSize="inherit"
											/>
										}
										onClick={() => setPaidModalOpen(true)}
										className={
											'player__paid-content-button'
										}
									/>
								</div>
							)}
						</div>
					</Fragment>
				)}
			</div>
		</div>
	);

	function initialVideoPlay() {
		if (isMounted.current && videoElement.current) {
			if (startVideo) {
				// hls.startLoad(startVideo);
				videoElement.current.currentTime = startVideo;
			}
			// hls.currentLevel = Math.floor((hls.levels.length - 1) / 2);
			hls.currentLevel = -1;
			setVideoReady(true);
			// Start the video
			play();
		}
	}

	function togglePlayPause() {
		if (isPaused) {
			play();
			setControlsActive(false);
			setUpNextShown(false);
		} else {
			pause();
		}
	}

	function play() {
		if (isPaidContent) {
			return;
		}

		applyVideoAction((video) => {
			setPlayError(false);
			const playPromise = video.play();
			// In browsers that don’t yet support this functionality,
			// playPromise won’t be defined.
			if (playPromise !== undefined) {
				playPromise.catch(function (error) {
					// Automatic playback failed.
					// Show a UI element to let the user manually start playback.
					setPlayError(true);
				});
			}
		});
	}

	function pause() {
		applyVideoAction((video) => {
			video.pause();
		});
	}
	// remains for a possible later use
	// function skipToStart() {
	// 	applyVideoAction((video) => {
	// 		video.currentTime = 0;
	// 		video.play();
	// 	});
	// }

	function back() {
		applyVideoAction((video) => {
			video.currentTime = Math.max(0, video.currentTime - 10);
		});
	}

	function forward() {
		applyVideoAction((video) => {
			video.currentTime = Math.min(
				video.duration,
				video.currentTime + 10,
			);
		});
	}

	// remains for a possible later use
	// function toggleVolume() {
	// 	if (isMuted) {
	// 		unmute();
	// 	} else {
	// 		mute();
	// 	}
	// }

	// function mute() {
	// 	applyVideoAction((video) => {
	// 		video.muted = true;
	// 	});
	// }

	// function unmute() {
	// 	applyVideoAction((video) => {
	// 		video.muted = false;
	// 	});
	// }

	function seek(
		_event: React.ChangeEvent<{}> | null,
		value: number | number[],
	) {
		// Convert the value into time
		if (typeof value === 'number') {
			setPlayPercentage(value);
			applyVideoAction((video) => {
				video.currentTime = value * video.duration;
			});
		}
	}

	function applyVideoAction(
		action: (video: HTMLVideoElement) => void,
	): boolean {
		if (videoElement.current && isMounted.current) {
			action(videoElement.current);
			return true;
		}
		return false;
	}

	function handlePlayerControlsClick(event: SyntheticEvent) {
		// This helps to handle situation when you click on any icon on the controls panel
		// but you don't want to hide it yet
		const isCurrentElementSourceOfEvent =
			event.target === event.currentTarget;
		if (isCurrentElementSourceOfEvent) {
			setControlsActive(false);
			setUpNextShown(false);
		}
	}
};
